import lodash from 'lodash';

import { AbrControllerEvents } from '@abrController/AbrController.event';
import { IEstimator } from '@abrController/Estimators/IEstimator';
import { TMaxResolutionLimits } from '@abrController/Estimators/MaxResolutionsEstimator/IMaxResolutionLimits';
import { EDebuggableModuleState, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import bind from '@utils/decorators/bind';

export class MaximalResolutionsEstimator implements IEstimator {
  public _eventBus: TEventBus;
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['abr'];

  private _lastAvailableResolutions: IResolution[] = [];
  private _currentResolutionsLimit: TMaxResolutionLimits = null;
  private _estimatedResolution: IResolution | null = null;
  private _onResolutionsConstraintsAvailableListener: string;

  constructor(eventBus: TEventBus) {
    this._eventBus = eventBus;
    this._onResolutionsConstraintsAvailableListener = this._eventBus.subscribe(
      AbrControllerEvents.resolutionsConstraintsAvailable,
      this._onResolutionsConstraintsAvailable,
    );
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onResolutionsConstraintsAvailableListener);
  }

  reset(): void {}

  public getNextEstimatedResolution(availableDecreasingResolutions: IResolution[]): IResolution | null {
    if (this._lastAvailableResolutions !== availableDecreasingResolutions) {
      this._lastAvailableResolutions = availableDecreasingResolutions;
      this._calculationAvailableResolution(this._currentResolutionsLimit);
    }

    return this._estimatedResolution;
  }

  @bind
  private _onResolutionsConstraintsAvailable(limit: TMaxResolutionLimits): void {
    if (!lodash.isEqual(this._currentResolutionsLimit, limit)) {
      this._calculationAvailableResolution(limit);
    }
  }

  private _meetsLimits(resolution: IResolution, limits: TMaxResolutionLimits): boolean {
    if (limits?.bitrate !== undefined && resolution.bitrate > limits?.bitrate) return false;
    if (limits?.height !== undefined && resolution.height > limits?.height) return false;
    if (limits?.width !== undefined && resolution.width > limits?.width) return false;
    if (limits?.fps !== undefined) {
      if (resolution.fps === undefined || +resolution.fps > +limits?.fps) return false;
    }
    return true;
  }

  private _calculationAvailableResolution(limits: TMaxResolutionLimits): void {
    this._currentResolutionsLimit = { ...limits };

    if (this._lastAvailableResolutions?.length === 0) {
      return;
    }

    if (limits === null) {
      this._estimatedResolution = null;
      return;
    }
    const filteredResolutions: IResolution[] = this._lastAvailableResolutions.filter(
      (availableResolution) => !limits || this._meetsLimits(availableResolution, limits),
    );
    this._estimatedResolution = filteredResolutions[0] || this._lastAvailableResolutions.slice(-1)[0];
  }
}
